import { ChangeDetectionStrategy, Component, ElementRef, inject, input } from '@angular/core';
import { MatDialogModule } from '@angular/material/dialog';
import { BzButton } from '../button/bz-button';
import { BzIcon } from '../icon/bz-icon';
import { BZ_DIALOG_RESIZABLE, BzDialogRef } from './bz-dialog-ref';
import { bzIconClose } from '@bringoz/ui/icons';

/**
 * Header / body / footer chrome for a dialog body.
 *
 * This is the piece worth sharing. The console repeats the same structure
 * about 437 times — `md-dialog` wrapping `md-dialog-content` and
 * `md-dialog-actions` — and every one of those hand-rolls its own padding,
 * scroll behaviour and footer alignment. A dialog written with this gets that
 * for free and stays consistent with the rest.
 *
 * Project content with the two attribute slots:
 *
 * ```html
 * <bz-dialog-layout heading="Edit note">
 *   <div bzDialogContent>…the form…</div>
 *   <div bzDialogActions>…buttons…</div>
 * </bz-dialog-layout>
 * ```
 *
 * The body stays in charge of its own actions, because only it knows what a
 * valid result looks like. This supplies the frame, not the behaviour.
 */
@Component({
  selector: 'bz-dialog-layout',
  imports: [MatDialogModule, BzButton, BzIcon],
  templateUrl: './bz-dialog-layout.html',
  styleUrl: './bz-dialog-layout.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class BzDialogLayout {
  private readonly ref = inject(BzDialogRef, { optional: true });
  private readonly host = inject<ElementRef<HTMLElement>>(ElementRef);

  /** Set by BzDialogService from `open(…, { resizable: true })`. */
  protected readonly resizable = inject(BZ_DIALOG_RESIZABLE);

  private start?: { x: number; y: number; width: number; height: number; pane: HTMLElement };

  readonly heading = input('');
  /** Secondary line under the heading. */
  readonly subheading = input('');
  /** Shows an × in the header. Off by default — a forced choice has no ×. */
  readonly dismissible = input(false);

  protected readonly closeIcon = bzIconClose;

  protected dismiss(): void {
    this.ref?.close(undefined);
  }

  /**
   * Drag-to-resize, on pointer events rather than CSS `resize`.
   *
   * CSS `resize: both` was tried first and does not work here: the browser
   * paints its grip on the element, but `elementFromPoint` at that corner
   * returns the dialog's own footer and buttons, which sit on top of it. The
   * grip is visible and unclickable — the kind of thing that only shows up
   * when you actually drag it, not when you set width/height in a test.
   *
   * The element being resized is `.cdk-overlay-pane`, which carries the
   * dialog's width and height. It is found by walking up from this host
   * rather than injected, because it belongs to Material's overlay and no
   * token exposes it.
   */
  protected onResizeStart(event: PointerEvent): void {
    const pane = this.host.nativeElement.closest<HTMLElement>('.cdk-overlay-pane');
    if (!pane) return;

    const rect = pane.getBoundingClientRect();
    this.start = { x: event.clientX, y: event.clientY, width: rect.width, height: rect.height, pane };

    // Pointer capture keeps events coming to the grip even when the cursor
    // leaves it, which it immediately does — without this the drag stops the
    // moment the pointer moves faster than the dialog grows.
    (event.target as HTMLElement).setPointerCapture(event.pointerId);
    event.preventDefault();
  }

  protected onResizeMove(event: PointerEvent): void {
    if (!this.start) return;
    const { x, y, width, height, pane } = this.start;
    // Only width/height are written; the CSS min-width/min-height on the pane
    // do the clamping, so there is no second copy of those limits here.
    pane.style.width = `${width + (event.clientX - x)}px`;
    pane.style.height = `${height + (event.clientY - y)}px`;
  }

  protected onResizeEnd(event: PointerEvent): void {
    if (!this.start) return;
    (event.target as HTMLElement).releasePointerCapture(event.pointerId);
    this.start = undefined;
  }
}
